import React from "react";
import { Navigate, RouteObject } from "react-router-dom";

import AirbrakeErrorBoundary from "@/components/containers/AirbrakeErrorBoundary";
import NotFoundPage from "@/components/static/NotFoundPage";
import { NavigationProvider } from "@/context/NavigationContext";

import { type Path, QUOTE } from "../paths";
import IndexRoute from "./IndexRoute";

export const LAZY_IMPORT_MAP: Partial<
  Record<Path, () => Promise<{ default: React.ComponentType }>>
> = {
  [QUOTE.DETAIL]: () => import("./detail/page"),
  [QUOTE.PAX]: () => import("./pax/page"),
  [QUOTE.DATE]: () => import("./date/page"),
  [QUOTE.DEPARTURE_AIRPORT]: () => import("./departureairport/page"),
  [QUOTE.FLIGHT_TIMES]: () => import("./flighttimes/page"),
};

const createRoute = (
  path: Path,
  children: RouteObject[] = [],
  overrides = {},
): RouteObject => {
  return {
    path,
    children,
    lazy: async () => {
      const importer = LAZY_IMPORT_MAP[path];

      if (!importer)
        throw new Error(`No lazy import registered for path: ${path}`);

      const { default: Component } = await importer();

      return {
        element: <Component />,
      };
    },
    ...overrides,
  };
};

const routes: RouteObject[] = [
  {
    path: "/",
    element: <Navigate to={QUOTE.ROOT} />,
  },
  {
    path: QUOTE.ROOT,
    element: (
      <NavigationProvider>
        <IndexRoute />
      </NavigationProvider>
    ),
    errorElement: <AirbrakeErrorBoundary />,
    children: [
      {
        element: <Navigate to={QUOTE.DETAIL} />,
        index: true,
      },
      createRoute(QUOTE.DETAIL),
      createRoute(QUOTE.PAX),
      createRoute(QUOTE.DATE),
      createRoute(QUOTE.DEPARTURE_AIRPORT),
      createRoute(QUOTE.FLIGHT_TIMES),
    ],
  },
  { path: "*", element: <NotFoundPage /> },
];

export default routes;
